// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module DashboardBuilder
 * @package @geenius/analytics/react-css
 * @description Renders the React CSS dashboard builder that composes
 * heterogeneous analytics widgets into a page-ready grid. The builder consumes
 * the shared dashboard layout vocabulary so consumers can declare dashboards as
 * data while preserving the standalone CSS implementation.
 */

import type {
  CohortDashboardWidget,
  ConsentSettings,
  DashboardLayout,
  DashboardWidget,
  FunnelResult,
  RealtimeStats,
  ReplayDashboardWidget,
  TimelineEvent,
} from "@geenius/analytics";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@geenius/ui/react-css";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { ChartCard } from "./ChartCard";
import { ConsentBanner } from "./ConsentBanner";
import { DeviceBreakdown } from "./DeviceBreakdown";
import { EventTable } from "./EventTable";
import { FunnelChart } from "./FunnelChart";
import { RealtimeBadge } from "./RealtimeBadge";
import { StatsCard } from "./StatsCard";
import { Timeline } from "./Timeline";
import { TopPagesTable } from "./TopPagesTable";

type AnalyticsCopy = ReturnType<typeof useAnalyticsCopy>;
type ReactCssDashboardSpan = DashboardWidget["span"];

/** Time range option shown by the React CSS dashboard builder toolbar. */
export interface DashboardBuilderTimeRangeOption {
  value: string;
  label: string;
}

interface ReactCssDashboardWidgetBase {
  id: string;
  title: string;
  description?: string;
  span?: ReactCssDashboardSpan;
}

/** React CSS dashboard widget that renders the variant's funnel chart component. */
export interface ReactCssFunnelDashboardWidget
  extends ReactCssDashboardWidgetBase {
  kind: "funnel";
  result: FunnelResult;
}

/** React CSS dashboard widget that renders the variant's realtime badge component. */
export interface ReactCssRealtimeDashboardWidget
  extends ReactCssDashboardWidgetBase {
  kind: "realtime";
  stats: RealtimeStats | null;
  isLoading?: boolean;
}

/** React CSS dashboard widget that renders the variant's consent banner component. */
export interface ReactCssConsentDashboardWidget
  extends ReactCssDashboardWidgetBase {
  kind: "consent";
  onAccept?: (settings: ConsentSettings) => void;
  onReject?: (settings: ConsentSettings) => void;
  show?: boolean;
}

/** React CSS dashboard widget that renders the variant's recent-activity timeline. */
export interface ReactCssTimelineDashboardWidget
  extends ReactCssDashboardWidgetBase {
  kind: "timeline";
  events: TimelineEvent[];
  maxVisible?: number;
}

/** Full widget vocabulary accepted by the React CSS dashboard builder. */
export type ReactCssDashboardWidget =
  | DashboardWidget
  | ReactCssFunnelDashboardWidget
  | ReactCssRealtimeDashboardWidget
  | ReactCssConsentDashboardWidget
  | ReactCssTimelineDashboardWidget;

/** Dashboard layout accepted by the React CSS dashboard builder. */
export interface ReactCssDashboardLayout
  extends Omit<DashboardLayout, "widgets"> {
  widgets: ReactCssDashboardWidget[];
}

/**
 * Props for rendering the React CSS dashboard builder.
 *
 * @property layout Shared dashboard layout definition or a raw widget array.
 * @property className Optional class name merged into the section wrapper.
 */
export interface DashboardBuilderProps {
  layout: ReactCssDashboardLayout | ReactCssDashboardWidget[];
  editable?: boolean;
  timeRange?: string;
  timeRangeOptions?: DashboardBuilderTimeRangeOption[];
  selectedWidgetId?: string;
  onLayoutChange?: (widgets: ReactCssDashboardWidget[]) => void;
  onTimeRangeChange?: (timeRange: string) => void;
  onWidgetSelect?: (widget: ReactCssDashboardWidget) => void;
  className?: string;
}

const DEFAULT_TIME_RANGE_OPTIONS: DashboardBuilderTimeRangeOption[] = [
  { value: "24h", label: "24 hours" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];

function moveWidget(
  widgets: ReactCssDashboardWidget[],
  widgetId: string,
  offset: number,
): ReactCssDashboardWidget[] {
  const fromIndex = widgets.findIndex((widget) => widget.id === widgetId);
  if (fromIndex < 0) return widgets;

  const toIndex = Math.max(0, Math.min(widgets.length - 1, fromIndex + offset));
  if (fromIndex === toIndex) return widgets;

  const next = [...widgets];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return widgets;
  next.splice(toIndex, 0, moved);
  return next;
}

function moveWidgetToTarget(
  widgets: ReactCssDashboardWidget[],
  draggedWidgetId: string,
  targetWidgetId: string,
): ReactCssDashboardWidget[] {
  const fromIndex = widgets.findIndex(
    (widget) => widget.id === draggedWidgetId,
  );
  const toIndex = widgets.findIndex((widget) => widget.id === targetWidgetId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return widgets;

  const next = [...widgets];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return widgets;
  next.splice(toIndex, 0, moved);
  return next;
}

function renderCohort(
  widget: CohortDashboardWidget,
  copy: AnalyticsCopy,
): React.ReactElement {
  return (
    <Card className="gn-analytics-dashboard-widget gn-analytics-dashboard-widget--cohort">
      <CardHeader>
        <CardTitle
          className="gn-analytics-dashboard-widget__title"
          data-analytics-dashboard-widget-title
        >
          {widget.title}
        </CardTitle>
        <CardDescription>
          {copy.plural(
            "analytics.dashboardBuilder.cohortDescription",
            widget.analysis.intervals.length,
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          className="gn-analytics-dashboard-widget__meta"
          data-analytics-cohort-overview
        >
          <Badge variant="secondary">
            {copy.plural(
              "analytics.dashboardBuilder.cohorts",
              widget.analysis.cohorts.length,
            )}
          </Badge>
          <Badge variant="secondary" data-analytics-cohort-intervals>
            {copy.t("analytics.dashboardBuilder.dayIntervals", {
              intervals: widget.analysis.intervals.join(", "),
            })}
          </Badge>
        </div>
        <div className="gn-analytics-cohort__grid" data-analytics-cohort-grid>
          {widget.analysis.cohorts.map((cohort) => (
            <Card
              key={cohort.cohort}
              className="gn-analytics-cohort__card"
              shadow="none"
            >
              <CardContent>
                <strong>{cohort.cohort}</strong>
                <div>
                  {copy.plural("analytics.dashboardBuilder.users", cohort.size)}
                </div>
                <div
                  className="gn-analytics-cohort__retention"
                  data-analytics-cohort-retention
                >
                  {cohort.retained.map((point) => (
                    <span
                      key={`${cohort.cohort}-${point.day}`}
                      className="gn-analytics-cohort__point"
                      data-analytics-cohort-point
                    >
                      <span>
                        {copy.t("analytics.dashboardBuilder.dayLabel", {
                          day: point.day,
                        })}
                      </span>
                      <span>{point.retentionRate.toFixed(1)}%</span>
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function renderReplay(
  widget: ReplayDashboardWidget,
  copy: AnalyticsCopy,
): React.ReactElement {
  return (
    <Card className="gn-analytics-dashboard-widget gn-analytics-dashboard-widget--replay">
      <CardHeader>
        <CardTitle
          className="gn-analytics-dashboard-widget__title"
          data-analytics-dashboard-widget-title
        >
          {widget.title}
        </CardTitle>
        <CardDescription>
          {copy.t("analytics.dashboardBuilder.replayDescription")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          className="gn-analytics-dashboard-widget__meta"
          data-analytics-session-replay-meta
        >
          <Badge variant="secondary">
            {copy.plural(
              "analytics.dashboardBuilder.frames",
              widget.replay.frames.length,
            )}
          </Badge>
          <Badge variant="secondary">
            {copy.t("analytics.dashboardBuilder.durationSeconds", {
              seconds: Math.round(widget.replay.duration / 1000),
            })}
          </Badge>
        </div>
        <ol
          className="gn-analytics-session-replay__list"
          data-analytics-session-replay-list
        >
          {widget.replay.frames.map((frame) => (
            <li
              key={frame.id}
              className="gn-analytics-session-replay__frame"
              data-analytics-session-replay-frame
            >
              <div className="gn-analytics-session-replay__frame-header">
                <strong>{frame.label}</strong>
                <span className="gn-analytics-session-replay__frame-time">
                  {copy.formatTime(frame.timestamp)}
                </span>
              </div>
              <span className="gn-analytics-session-replay__frame-url">
                {frame.url}
              </span>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

function renderWidget(
  widget: ReactCssDashboardWidget,
  copy: AnalyticsCopy,
): React.ReactElement | null {
  switch (widget.kind) {
    case "metric":
      return (
        <StatsCard
          label={widget.title}
          value={widget.metric.value}
          change={
            widget.metric.changePercent !== undefined
              ? `${widget.metric.changePercent.toFixed(1)}%`
              : undefined
          }
          trend={
            widget.metric.trend === "flat" ? "neutral" : widget.metric.trend
          }
        />
      );
    case "chart":
      return (
        <ChartCard
          title={widget.title}
          data={widget.data}
          type={widget.chartType}
        />
      );
    case "top-pages":
      return <TopPagesTable pages={widget.pages} />;
    case "device-breakdown":
      return <DeviceBreakdown breakdown={widget.breakdown} />;
    case "events":
      return <EventTable events={widget.events} maxRows={widget.maxRows} />;
    case "cohort":
      return renderCohort(widget, copy);
    case "replay":
      return renderReplay(widget, copy);
    case "funnel":
      return <FunnelChart result={widget.result} />;
    case "realtime":
      return (
        <Card className="gn-analytics-dashboard-widget gn-analytics-dashboard-widget--realtime">
          <CardHeader>
            <CardTitle
              className="gn-analytics-dashboard-widget__title"
              data-analytics-dashboard-widget-title
            >
              {widget.title}
            </CardTitle>
            {widget.description && (
              <CardDescription>{widget.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <RealtimeBadge
              stats={widget.stats}
              isLoading={widget.isLoading ?? false}
            />
          </CardContent>
        </Card>
      );
    case "consent":
      return (
        <ConsentBanner
          onAccept={widget.onAccept ?? (() => undefined)}
          onReject={widget.onReject ?? (() => undefined)}
          show={widget.show}
        />
      );
    case "timeline":
      return (
        <Timeline
          events={widget.events}
          maxVisible={widget.maxVisible}
          ariaLabel={widget.title}
        />
      );
    default:
      return null;
  }
}

/**
 * Renders the React CSS dashboard builder from a shared layout definition.
 *
 * @param props Dashboard layout data and an optional wrapper className.
 * @returns A section element containing the resolved analytics widget grid.
 */
export function DashboardBuilder({
  layout,
  editable = false,
  timeRange,
  timeRangeOptions = DEFAULT_TIME_RANGE_OPTIONS,
  selectedWidgetId,
  onLayoutChange,
  onTimeRangeChange,
  onWidgetSelect,
  className,
}: DashboardBuilderProps): React.ReactElement {
  const resolved = useMemo(
    () => (Array.isArray(layout) ? { widgets: layout } : layout),
    [layout],
  );
  const [editableWidgets, setEditableWidgets] = useState<
    ReactCssDashboardWidget[]
  >(resolved.widgets);
  const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
  const [internalTimeRange, setInternalTimeRange] = useState(
    timeRange ?? timeRangeOptions[1]?.value ?? "7d",
  );
  const [internalSelectedWidgetId, setInternalSelectedWidgetId] = useState<
    string | null
  >(selectedWidgetId ?? null);
  const copy = useAnalyticsCopy();
  const widgets = editable ? editableWidgets : resolved.widgets;
  const activeTimeRange = timeRange ?? internalTimeRange;
  const activeSelectedWidgetId =
    selectedWidgetId ?? internalSelectedWidgetId ?? widgets[0]?.id ?? null;
  const selectedWidget =
    widgets.find((widget) => widget.id === activeSelectedWidgetId) ?? null;

  useEffect(() => {
    setEditableWidgets(resolved.widgets);
  }, [resolved.widgets]);

  useEffect(() => {
    if (timeRange !== undefined) {
      setInternalTimeRange(timeRange);
    }
  }, [timeRange]);

  useEffect(() => {
    if (selectedWidgetId !== undefined) {
      setInternalSelectedWidgetId(selectedWidgetId);
    }
  }, [selectedWidgetId]);

  const applyWidgetOrder = (nextWidgets: ReactCssDashboardWidget[]): void => {
    setEditableWidgets(nextWidgets);
    onLayoutChange?.(nextWidgets);
  };

  const handleMoveWidget = (widgetId: string, offset: number): void => {
    applyWidgetOrder(moveWidget(widgets, widgetId, offset));
  };

  const handleSelectWidget = (widget: ReactCssDashboardWidget): void => {
    setInternalSelectedWidgetId(widget.id);
    onWidgetSelect?.(widget);
  };

  const handleTimeRangeChange = (nextTimeRange: string): void => {
    setInternalTimeRange(nextTimeRange);
    onTimeRangeChange?.(nextTimeRange);
  };

  return (
    <section
      className={cx("gn-analytics-dashboard-builder", className)}
      data-analytics-dashboard-builder
    >
      {(resolved.title || resolved.description) && (
        <header
          className="gn-analytics-dashboard-builder__header"
          data-analytics-dashboard-builder-header
        >
          {resolved.title && (
            <h2 className="gn-analytics-dashboard-builder__title">
              {resolved.title}
            </h2>
          )}
          {resolved.description && (
            <p className="gn-analytics-dashboard-builder__description">
              {resolved.description}
            </p>
          )}
        </header>
      )}
      {editable ? (
        <div
          className="gn-analytics-dashboard-builder__toolbar"
          data-analytics-dashboard-builder-toolbar
        >
          <label className="gn-analytics-dashboard-builder__time-range">
            <span>{copy.t("analytics.dashboardBuilder.timeRangeAria")}</span>
            <select
              className="gn-analytics-dashboard-builder__select"
              value={activeTimeRange}
              onChange={(event) =>
                handleTimeRangeChange(event.currentTarget.value)
              }
            >
              {timeRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <span className="gn-analytics-dashboard-builder__toolbar-summary">
            {copy.plural("analytics.dashboardBuilder.widgets", widgets.length)}
          </span>
        </div>
      ) : null}
      <div
        className="gn-analytics-dashboard-builder__grid"
        data-analytics-dashboard-builder-grid
      >
        {widgets.map((widget, index) => (
          <fieldset
            key={widget.id}
            className={cx(
              "gn-analytics-dashboard-builder__cell",
              editable && "gn-analytics-dashboard-builder__cell--editable",
              activeSelectedWidgetId === widget.id &&
                "gn-analytics-dashboard-builder__cell--selected",
            )}
            data-analytics-dashboard-builder-cell
            data-span={widget.span ?? 1}
            draggable={editable}
            onDragStart={() => setDraggedWidgetId(widget.id)}
            onDragOver={(event) => {
              if (editable) event.preventDefault();
            }}
            onDrop={(event) => {
              event.preventDefault();
              if (draggedWidgetId) {
                applyWidgetOrder(
                  moveWidgetToTarget(widgets, draggedWidgetId, widget.id),
                );
              }
              setDraggedWidgetId(null);
            }}
          >
            <legend className="gn-analytics-a11y__sr-only">
              {copy.t("analytics.dashboardBuilder.widgetLegend", {
                title: widget.title,
              })}
            </legend>
            {editable ? (
              <div
                className="gn-analytics-dashboard-builder__cell-controls"
                data-analytics-dashboard-widget-controls
              >
                <button
                  type="button"
                  className="gn-analytics-dashboard-builder__control"
                  disabled={index === 0}
                  onClick={() => handleMoveWidget(widget.id, -1)}
                >
                  {copy.t("analytics.dashboardBuilder.moveWidgetUp", {
                    title: widget.title,
                  })}
                </button>
                <button
                  type="button"
                  className="gn-analytics-dashboard-builder__control"
                  disabled={index === widgets.length - 1}
                  onClick={() => handleMoveWidget(widget.id, 1)}
                >
                  {copy.t("analytics.dashboardBuilder.moveWidgetDown", {
                    title: widget.title,
                  })}
                </button>
                <button
                  type="button"
                  className="gn-analytics-dashboard-builder__control"
                  onClick={() => handleSelectWidget(widget)}
                >
                  {copy.t("analytics.dashboardBuilder.editWidget", {
                    title: widget.title,
                  })}
                </button>
              </div>
            ) : null}
            {renderWidget(widget, copy)}
          </fieldset>
        ))}
      </div>
      {editable && selectedWidget ? (
        <aside
          className="gn-analytics-dashboard-builder__editor"
          data-analytics-dashboard-widget-editor
          aria-label={copy.t("analytics.dashboardBuilder.widgetEditor")}
        >
          <h3 className="gn-analytics-dashboard-builder__editor-title">
            {copy.t("analytics.dashboardBuilder.widgetEditor")}
          </h3>
          <dl className="gn-analytics-dashboard-builder__editor-details">
            <div>
              <dt>{copy.t("analytics.dashboardBuilder.titleLabel")}</dt>
              <dd>{selectedWidget.title}</dd>
            </div>
            <div>
              <dt>{copy.t("analytics.dashboardBuilder.kindLabel")}</dt>
              <dd>{selectedWidget.kind}</dd>
            </div>
            <div>
              <dt>{copy.t("analytics.dashboardBuilder.spanLabel")}</dt>
              <dd>{selectedWidget.span ?? 1}</dd>
            </div>
            {selectedWidget.description ? (
              <div>
                <dt>{copy.t("analytics.dashboardBuilder.descriptionLabel")}</dt>
                <dd>{selectedWidget.description}</dd>
              </div>
            ) : null}
          </dl>
        </aside>
      ) : null}
    </section>
  );
}
